import React, { useEffect, useState } from "react";
import { FormControl, InputAdornment, InputLabel, MenuItem, Select } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import PersonOutlineRoundedIcon from "@mui/icons-material/PersonOutlineRounded";
import { useSelector } from "react-redux";
import { GetProfsForDropdownCall } from "../../redux/api/professor.api";

/**
 * Shows a "Select professor" dropdown when the current user is admin or university_admin.
 * Optional: include "My content" (value "") so they can view their own data when applicable.
 * @param {string} value - selected prof id or ""
 * @param {function} onChange - (profId: string) => void
 * @param {object} sx - MUI sx prop
 * @param {boolean} includeMyContent - if true, first option is "My content" (no prof_id)
 */
export default function ProfSelector({ value, onChange, sx = {}, includeMyContent = true }) {
  const theme = useTheme();
  const role = useSelector((state) => state?.auth?.role);
  const [profs, setProfs] = useState([]);
  const [loading, setLoading] = useState(false);

  const showSelector = role === "admin" || role === "university_admin";

  useEffect(() => {
    if (!showSelector) return;
    setLoading(true);
    GetProfsForDropdownCall()
      .then((list) => setProfs(Array.isArray(list) ? list : []))
      .finally(() => setLoading(false));
  }, [showSelector]);

  if (!showSelector) return null;

  const displayValue = value ?? "";

  return (
    <FormControl
      size="small"
      sx={{
        minWidth: 240,
        "& .MuiOutlinedInput-root": {
          minHeight: 44,
          borderRadius: 2.5,
          bgcolor: theme.palette.background.paper || "#FFFFFF",
          boxShadow: "0 4px 14px rgba(15, 23, 42, 0.06)",
          transition: "box-shadow 0.2s ease, border-color 0.2s ease",
          "& fieldset": {
            borderColor: "rgba(148, 163, 184, 0.35)",
          },
          "&:hover fieldset": {
            borderColor: alpha(theme.palette.primary.main, 0.45),
          },
          "&.Mui-focused": {
            boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.16)}`,
            "& fieldset": {
              borderColor: theme.palette.primary.main,
              borderWidth: 1,
            },
          },
        },
        "& .MuiInputLabel-root": {
          fontWeight: 600,
          color: theme.palette.text.secondary,
        },
        ...sx,
      }}
    >
      <InputLabel id="prof-selector-label" shrink>
        Professor
      </InputLabel>
      <Select
        labelId="prof-selector-label"
        label="Professor"
        value={displayValue}
        displayEmpty
        renderValue={(selected) => {
          if (!selected) {
            return includeMyContent ? "My content" : "Select professor";
          }
          const p = profs.find((x) => String(x.id) === String(selected));
          if (!p) return String(selected);
          return (
            [p.first_name, p.last_name].filter(Boolean).join(" ") ||
            p.email ||
            p.id
          );
        }}
        onChange={(e) => onChange(e.target.value)}
        disabled={loading}
        notched
        startAdornment={
          <InputAdornment position="start">
            <PersonOutlineRoundedIcon
              sx={{
                fontSize: 20,
                color: theme.palette.primary.main,
                opacity: 0.9,
              }}
            />
          </InputAdornment>
        }
        MenuProps={{
          PaperProps: {
            sx: {
              borderRadius: 2.5,
              mt: 1,
              border: "1px solid rgba(148, 163, 184, 0.2)",
              boxShadow: "0 12px 32px rgba(15, 23, 42, 0.12)",
            },
          },
        }}
      >
        {includeMyContent && (
          <MenuItem value="" sx={{ borderRadius: 1.5, mx: 0.5, my: 0.25 }}>
            My content
          </MenuItem>
        )}
        {profs.map((p) => (
          <MenuItem
            key={p.id}
            value={p.id}
            sx={{ borderRadius: 1.5, mx: 0.5, my: 0.25 }}
          >
            {[p.first_name, p.last_name].filter(Boolean).join(" ") || p.email || p.id}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}
